import React, { useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Button, Icon, Image, Input, Loader, Message, Modal
} from 'semantic-ui-react';
import { useStore } from '../../app/stores/store';
import { useTranslation } from 'react-i18next';
import { agent } from '../../app/api/agent';
import debounce from 'lodash/debounce';
import { TeamSearchResultDto } from '../../app/models/Team';

interface Props {
  teamId: string;
  sportId: string;
  trigger?: React.ReactNode;
}

export const CreateChallengeModal = observer(function CreateChallengeModal({
  teamId,
  sportId,
  trigger,
}: Props) {
  const { teamMatchStore } = useStore();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'direct' | 'open'>('direct');
  const [scheduledAt, setScheduledAt] = useState('');

  // Opponent search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<TeamSearchResultDto[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedOpponent, setSelectedOpponent] = useState<TeamSearchResultDto | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // ── Debounced search against the server ──────────────────────────────────────

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const doSearch = useCallback(
    debounce(async (q: string) => {
      if (q.trim().length < 2) {
        setSearchResults([]);
        setDropdownOpen(false);
        setSearching(false);
        return;
      }
      setSearching(true);
      try {
        const results = await agent.TeamMatches.searchOpponents(teamId, q.trim());
        setSearchResults(results);
        setDropdownOpen(true);
      } catch {
        setSearchResults([]);
        setDropdownOpen(false);
      } finally {
        setSearching(false);
      }
    }, 350),
    [teamId]
  );

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    // If the user edits the field after picking a team, clear the selection
    if (selectedOpponent && value !== selectedOpponent.name) {
      setSelectedOpponent(null);
    }
    doSearch(value);
  };

  const handleSelectOpponent = (team: TeamSearchResultDto) => {
    setSelectedOpponent(team);
    setSearchQuery(team.name);
    setDropdownOpen(false);
    setSearchResults([]);
  };

  const handleClearSelection = () => {
    setSelectedOpponent(null);
    setSearchQuery('');
    setSearchResults([]);
    setDropdownOpen(false);
  };

  // ── Submit ────────────────────────────────────────────────────────────────────

  const handleCreate = async () => {
    let newId: string | null = null;

    if (mode === 'direct') {
      if (!selectedOpponent) return;
      newId = await teamMatchStore.createDirectChallenge({
        teamAId: teamId,
        teamBId: selectedOpponent.id,
        scheduledAt: scheduledAt || undefined,
      });
    } else {
      newId = await teamMatchStore.createOpenChallenge({
        teamAId: teamId,
        scheduledAt: scheduledAt || undefined,
      });
    }

    if (newId) handleClose();
  };

  const handleClose = () => {
    setOpen(false);
    setMode('direct');
    setScheduledAt('');
    setSearchQuery('');
    setSelectedOpponent(null);
    setSearchResults([]);
    setDropdownOpen(false);
    doSearch.cancel();
  };

  const canSubmit =
    !teamMatchStore.submitting &&
    (mode === 'open' || (mode === 'direct' && !!selectedOpponent));

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Modal
      trigger={
        trigger ?? (
          <Button
            primary
            size="small"
            style={{ borderRadius: 50 }}
            onClick={() => setOpen(true)}
          >
            <Icon name="shield alternate" />
            {t('teamMatch.challenge')}
          </Button>
        )
      }
      open={open}
      onClose={handleClose}
      size="small"
      closeIcon
    >
      <Modal.Header>
        <Icon name="shield alternate" />
        {t('teamMatch.createModal.title')}
      </Modal.Header>

      <Modal.Content>
        {/* ── Mode toggle ─────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
          <Button
            toggle
            active={mode === 'direct'}
            color={mode === 'direct' ? 'teal' : undefined}
            basic={mode !== 'direct'}
            onClick={() => setMode('direct')}
          >
            <Icon name="lock" />
            {t('teamMatch.createModal.direct')}
          </Button>
          <Button
            toggle
            active={mode === 'open'}
            color={mode === 'open' ? 'blue' : undefined}
            basic={mode !== 'open'}
            onClick={() => setMode('open')}
          >
            <Icon name="unlock" />
            {t('teamMatch.createModal.open')}
          </Button>
        </div>

        {/* ── Direct: opponent search ──────────────────────────────────────── */}
        {mode === 'direct' && (
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontWeight: 700, marginBottom: 6, fontSize: '0.9em' }}>
              {t('teamMatch.createModal.searchOpponent')}
            </label>

            {/* Selected opponent badge */}
            {selectedOpponent && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                background: '#f0fdf4',
                border: '1.5px solid #22c55e',
                borderRadius: 10,
                marginBottom: 10,
              }}>
                <Image
                  src={selectedOpponent.logoUrl || '/assets/team-placeholder.png'}
                  avatar
                  style={{ width: 34, height: 34, objectFit: 'cover', flexShrink: 0 }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.95em' }}>{selectedOpponent.name}</div>
                  <div style={{ fontSize: '0.78em', color: '#555' }}>
                    <Icon name="user" size="small" />
                    {selectedOpponent.memberCount} {t('teams.members')}
                  </div>
                </div>
                <Button
                  size="mini"
                  basic
                  icon="close"
                  title={t('teamMatch.createModal.clearSelection')}
                  onClick={handleClearSelection}
                />
              </div>
            )}

            {/* Search input — hidden once a team is selected */}
            {!selectedOpponent && (
              <div style={{ position: 'relative' }}>
                <Input
                  fluid
                  loading={searching}
                  icon={searching ? undefined : 'search'}
                  placeholder={t('teamMatch.createModal.searchOpponentPlaceholder')}
                  value={searchQuery}
                  onChange={e => handleSearchChange(e.target.value)}
                  autoComplete="off"
                />

                {/* Dropdown */}
                {dropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    background: '#fff',
                    border: '1px solid #e0e0e0',
                    borderTop: 'none',
                    borderRadius: '0 0 8px 8px',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                    maxHeight: 270,
                    overflowY: 'auto',
                  }}>
                    {/* Searching indicator */}
                    {searching && (
                      <div style={{
                        padding: '14px 16px',
                        textAlign: 'center',
                        color: '#888',
                        fontSize: '0.88em',
                      }}>
                        <Loader active inline size="mini" style={{ marginRight: 8 }} />
                        {t('teamMatch.createModal.searchingTeams')}
                      </div>
                    )}

                    {/* No results */}
                    {!searching && searchResults.length === 0 && searchQuery.trim().length >= 2 && (
                      <div style={{
                        padding: '14px 16px',
                        textAlign: 'center',
                        color: '#aaa',
                        fontSize: '0.88em',
                      }}>
                        <Icon name="search" />
                        {t('teamMatch.createModal.noTeamsFound')}
                      </div>
                    )}

                    {/* Result rows */}
                    {!searching && searchResults.map(team => (
                      <button
                        key={team.id}
                        onClick={() => handleSelectOpponent(team)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          width: '100%',
                          padding: '10px 14px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f5f5f5',
                          background: 'transparent',
                          border: 'none',
                          textAlign: 'left',
                          transition: 'background 0.12s',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#f5f8ff')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        <Image
                          src={team.logoUrl || '/assets/team-placeholder.png'}
                          avatar
                          style={{ width: 36, height: 36, objectFit: 'cover', flexShrink: 0 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontWeight: 600,
                            fontSize: '0.92em',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {team.name}
                          </div>
                          <div style={{ fontSize: '0.76em', color: '#888', marginTop: 1 }}>
                            <Icon name="user" size="small" />
                            {team.memberCount} {t('teams.members')}
                          </div>
                        </div>
                        <Icon name="chevron right" color="grey" size="small" style={{ flexShrink: 0 }} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Hint when nothing typed yet */}
            {!selectedOpponent && searchQuery.trim().length < 2 && (
              <div style={{ fontSize: '0.8em', color: '#aaa', marginTop: 6 }}>
                <Icon name="info circle" />
                {t('teamMatch.createModal.typeToSearch')}
              </div>
            )}
          </div>
        )}

        {/* ── Open challenge hint ──────────────────────────────────────────── */}
        {mode === 'open' && (
          <Message info style={{ marginBottom: 20 }}>
            <Icon name="info circle" />
            {t('teamMatch.createModal.openHint')}
          </Message>
        )}

        {/* ── Scheduled date/time ─────────────────────────────────────────── */}
        <div>
          <label style={{ display: 'block', fontWeight: 700, marginBottom: 6, fontSize: '0.9em' }}>
            {t('teamMatch.createModal.scheduledAt')}
          </label>
          <Input
            type="datetime-local"
            value={scheduledAt}
            onChange={e => setScheduledAt(e.target.value)}
            fluid
          />
        </div>
      </Modal.Content>

      <Modal.Actions>
        <Button basic onClick={handleClose} disabled={teamMatchStore.submitting}>
          {t('teams.cancel')}
        </Button>
        <Button
          primary
          loading={teamMatchStore.submitting}
          disabled={!canSubmit}
          onClick={handleCreate}
        >
          <Icon name="send" />
          {mode === 'direct'
            ? t('teamMatch.createModal.sendChallenge')
            : t('teamMatch.createModal.publishChallenge')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
});